"use strict";

define(["dojo/_base/declare", "dojo/_base/lang", "./Observable", "./PeerEventObservable", "mw-utils/Utils", "mw-log/Log"], function (declare, lang, Observable, PeerEventObservable, Utils, Log) {
  //noinspection FunctionWithInconsistentReturnsJS,JSUnusedLocalSymbols
  /**
   * Node of a peer model. A node is the basic building block of a peer model. The peer model data
   * structure is a basic tree (not a graph). Nodes have properties and child nodes. They also
   * have a required id and a type. There is 1 root node per model namespace
   * (see PeerModelManager).
   */
  //noinspection FunctionWithInconsistentReturnsJS,JSUnusedLocalSymbols
  var PeerNode = declare([PeerEventObservable], {
    /**
     * Unique identifier of this peer node. The id is the same for all corresponding peer nodes
     * for both the client and server.
     * @return Unique identifier for this node.
     */
    getId: function getId() {},
    /**
     * Peer node type. The type is a user specified identifier which can be used to specify what
     * type of component this node corresponds to.
     * @return User specified node type.
     */
    getType: function getType() {},
    /**
     * Set a property on the node. A property can be anything that is JSON serializable.
     * When a property is changed, an event is dispatched.
     * @param key A key to reference the property by.
     * @param value The value of the property.
     * @param originator Optional originator for event fired during this operation.
     */
    setProperty: function setProperty(key, value, originator) {},
    /**
     * Get the value of a property.
     * @param key The key that the property is stored under.
     * @return The value of the property.
     */
    getProperty: function getProperty(key) {},
    /**
     * Check if a property is set.
     * @param key The key to test.
     * @return True if the property has been set (even if it is set to null), false otherwise.
     */
    hasProperty: function hasProperty(key) {},
    /**
     * Unset the specified property. After unsetting, hasProperty will return false.
     * @param key The key that the property is stored under.
     * @param originator Optional originator for event fired during this operation.
     */
    unsetProperty: function unsetProperty(key, originator) {},
    /**
     * Set a group of properties. Each property is set individually. Any existing properties
     * will be updated.
     * @param properties Object of the new properties.
     * @param originator Optional originator for event fired during this operation.
     */
    setProperties: function setProperties(properties, originator) {},
    /**
     * Unset a group of properties
     * @param keys String array of keys
     * @param originator Optional originator for event fired during this operation.
     */
    unsetProperties: function unsetProperties(keys, originator) {},
    /**
     * Replace all existing properties with a new set of properties. Properties which exist in
     * both places will be updated. Properties which do not exist in the new object will be
     * removed.
     * @param properties New properties.
     * @param originator Optional originator for event fired during this operation.
     */
    replaceProperties: function replaceProperties(properties, originator) {},
    /**
     * Get access to the properties object. Properties which are added after this method is
     * called will be reflected in the returned object.
     * @return Unmodifiable properties.
     */
    getProperties: function getProperties() {},
    /**
     * Add a new child of the specified type at the specified location.
     * @param type The type of the new node.
     * @param properties Optional object containing the initial set of properties for the new
     * node.
     * @param index Optional location in the array of children.
     * @param originator Optional originator for event fired during this operation.
     * @return A reference to the new node.
     */
    addChild: function addChild(type, properties, index, originator) {},
    /**
     * Check if node has the specified child node.
     * @param childNodeOrId Test node or the ID of node.
     * @return True if the node is a direct child of this node.
     */
    hasChild: function hasChild(childNodeOrId) {},
    /**
     * Get the child at the specified location in the children array.
     * @param indexOrId Location of the child (index) or the ID of a child.
     * @return The child node.
     */
    getChild: function getChild(indexOrId) {},
    /**
     * Get the child node's index in the parent node.
     * @param node Child node.
     * @return The index of the child node. -1 if it isn't a child.
     */
    getChildIndex: function getChildIndex(node) {},
    /**
     * Get an array of all the children of the node. Changes in the children of the node after
     * this method is called will be reflected in the returned array.
     * @return The array of child nodes.
     */
    getChildren: function getChildren() {},
    /**
     * Has a descendant node with the specified ID.
     * @param id The node id.
     * @return Returns true if the node has a descendant node with the same ID, false otherwise.
     */
    hasDescendant: function hasDescendant(id) {},
    /**
     * Return the descendant node with the specified ID.
     * @param id The ID of the descendant node.
     * @return The descendant node or null if it doesn't exist.
     */
    getDescendant: function getDescendant(id) {},
    /**
     * Get the number of children of this node.
     * @return The number of children.
     */
    getNumberOfChildren: function getNumberOfChildren() {},
    /**
     * Check if a node has any detached children.
     * @return True if there are any detached children, false otherwise.
     */
    hasDetachedChildren: function hasDetachedChildren() {},
    /**
     * Get an array of all the detached children of the node. Changes in the children of the
     * node after this method is called will be reflected in the returned array.
     * @return The array of detached nodes.
     */
    getDetachedChildren: function getDetachedChildren() {},
    /**
     * Detach this node from it's parent. It can be reattached later using the reattach method
     * on the detached node.
     * @param originator Optional originator for event fired during this operation.
     */
    detach: function detach(originator) {},
    /**
     * Reattach the node the it's previous parent at the specified index.
     * @param index Optional index to place the node at in it's parent. Will be added to the end
     * if not specified.
     * @param originator Optional originator for event fired during this operation.
     */
    reattach: function reattach(index, originator) {},
    /**
     * Check if this node is detached.
     * @return True if this node is detached.
     */
    isDetached: function isDetached() {},
    /**
     * Check if any of this nodes ancestors are detached.
     * @return True if an ancestor is detached.
     */
    isAncestorDetached: function isAncestorDetached() {},
    /**
     * Destroy this node and remove it from it's parent. First destroys all of it's children.
     * @param originator Optional originator for event fired during this operation.
     */
    destroy: function destroy(originator) {},
    /**
     * Remove this node from it's parent.
     * @deprecated remove() is deprecated in favor of destroy().
     */
    remove: function remove() {},
    /**
     * Check if the node has a parent. The root node of a model never has a parent.
     * @return True if the node has a parent, false otherwise.
     */
    hasParent: function hasParent() {},
    /**
     * Get the parent of this node.
     * @return The parent node.
     */
    getParent: function getParent() {},
    /**
     * Return the root of the model (the parent's parent's parent.... until there are
     * no more parents).
     * @return The root node.
     */
    getRoot: function getRoot() {},
    /**
     * Accept the visitor.
     * @param visitor The visitor function.
     */
    accept: function accept(visitor) {}
  });
  var PeerNodeImpl;
  PeerNodeImpl = declare([PeerNode, PeerEventObservable.Impl, Observable.Syncable], {
    constructor: function constructor(id, type, properties, idCache) {
      if (!lang.isString(id) || !lang.isString(type)) {
        throw new Error("Node type and ID must be strings");
      }
      this._type = type;
      this._id = id;
      this._idCache = idCache || {};
      this._idCache[id] = this;
      this._properties = {};
      if (properties) {
        this._setProperties(properties, undefined, true);
      }
      this._children = [];
      this._detached = [];
    },
    getId: function getId() {
      return this._id;
    },
    getType: function getType() {
      return this._type;
    },
    setProperty: function setProperty(key, value, originator) {
      if (value === null || value === undefined) {
        throw new Error("Illegal argument. Value must not be null or undefined.");
      }
      var oldValue = this._setPropertyAndDispatchPropertySetEvent(key, value, originator);
      this._dispatchSinglePropertiesSetEvent(key, oldValue, value, originator);
    },
    _setPropertyAndDispatchPropertySetEvent: function _setPropertyAndDispatchPropertySetEvent(key, value, originator, suppressEvents) {
      var oldValue = this._properties[key];
      if (oldValue !== value) {
        this._properties[key] = value;
        if (!suppressEvents) {
          this.dispatchEvent("propertySet", this, {
            key: key,
            oldValue: oldValue,
            newValue: value
          }, originator);
        }
      }
      return oldValue;
    },
    _dispatchSinglePropertiesSetEvent: function _dispatchSinglePropertiesSetEvent(key, oldValue, newValue, originator) {
      if (oldValue !== newValue) {
        var propertiesMap = {
          oldValues: {},
          newValues: {}
        };
        propertiesMap.oldValues[key] = oldValue;
        propertiesMap.newValues[key] = newValue;
        this.dispatchEvent("propertiesSet", this, propertiesMap, originator);
      }
    },
    unsetProperty: function unsetProperty(key, originator) {
      var oldValue = this._unsetPropertyAndDispatchPropertyUnsetEvent(key, originator);
      this._dispatchSinglePropertiesUnsetEvent(key, oldValue, originator);
    },
    unsetProperties: function unsetProperties(keys, originator) {
      var propertiesMap = {
        oldValues: {}
      };
      keys.forEach(function (key) {
        propertiesMap.oldValues[key] = this._unsetPropertyAndDispatchPropertyUnsetEvent(key, originator);
      }, this);
      this.dispatchEvent("propertiesUnset", this, propertiesMap, originator);
    },
    _unsetPropertyAndDispatchPropertyUnsetEvent: function _unsetPropertyAndDispatchPropertyUnsetEvent(key, originator) {
      var oldValue;
      if (this.hasProperty(key)) {
        oldValue = this._properties[key];
        delete this._properties[key];
        this.dispatchEvent("propertyUnset", this, {
          key: key,
          oldValue: oldValue
        }, originator);
      }
      return oldValue;
    },
    _dispatchSinglePropertiesUnsetEvent: function _dispatchSinglePropertiesUnsetEvent(key, oldValue, originator) {
      var propertiesMap = {
        oldValues: {}
      };
      propertiesMap.oldValues[key] = oldValue;
      this.dispatchEvent("propertiesUnset", this, propertiesMap, originator);
    },
    hasProperty: function hasProperty(key) {
      return this._properties.hasOwnProperty(key);
    },
    getProperty: function getProperty(key) {
      return this._properties[key];
    },
    setProperties: function setProperties(properties, originator) {
      this._setProperties(properties, originator, false);
    },
    _setProperties: function _setProperties(properties, originator, suppressEvents) {
      var key, value, newValue, oldValue;
      for (key in properties) {
        if (properties.hasOwnProperty(key)) {
          value = properties[key];
          if (value === null || value === undefined) {
            Log.warn("Illegal argument. Property value must not be null or " + "undefined. This will become an exception in the future.");
          }
        }
      }
      var propertiesChanged = false;
      var propertiesMap = {
        oldValues: {},
        newValues: {}
      };
      for (key in properties) {
        if (properties.hasOwnProperty(key)) {
          newValue = properties[key];
          oldValue = this._setPropertyAndDispatchPropertySetEvent(key, newValue, originator, suppressEvents);
          if (oldValue !== newValue) {
            propertiesChanged = true;
            propertiesMap.oldValues[key] = oldValue;
            propertiesMap.newValues[key] = newValue;
          }
        }
      }
      if (propertiesChanged && !suppressEvents) {
        this.dispatchEvent("propertiesSet", this, propertiesMap, originator);
      }
    },
    replaceProperties: function replaceProperties(properties, originator) {
      var key;
      this.setProperties(properties, originator);
      for (key in this._properties) {
        if (this._properties.hasOwnProperty(key) && !properties.hasOwnProperty(key)) {
          this.unsetProperty(key, originator);
        }
      }
    },
    getProperties: function getProperties() {
      var key;
      var properties = {};
      // make a copy of the properties map:
      for (key in this._properties) {
        if (this._properties.hasOwnProperty(key) && this.hasProperty(key)) {
          properties[key] = this.getProperty(key);
        }
      }
      return properties;
    },
    addChild: function addChild(type, properties, index, originator) {
      if (typeof properties === "number") {
        index = properties;
        properties = null;
      }
      var node = PeerNodeImpl.createNode(type, properties, this._idCache);
      index = this._addChild(node, index);
      this.dispatchEvent("childAdded", this, {
        child: node,
        index: index
      }, originator);
      return node;
    },
    addChildWithId: function addChildWithId(id, type, properties, index, originator) {
      var node = new PeerNodeImpl(id, type, properties, this._idCache);
      index = this._addChild(node, index);
      this.dispatchEvent("childAdded", this, {
        child: node,
        index: index || 0
      }, originator);
      return node;
    },
    hasChild: function hasChild(nodeOrId) {
      if (lang.isString(nodeOrId)) {
        var i,
          child,
          childrenLength = this._children.length;
        for (i = 0; i < childrenLength; i += 1) {
          child = this._children[i];
          if (child.getId() === nodeOrId) {
            return true;
          }
        }
        return false;
      } else {
        var index = this.getChildIndex(nodeOrId);
        return index >= 0 && this._children[index];
      }
    },
    getChild: function getChild(indexOrId) {
      if (lang.isString(indexOrId)) {
        var i,
          child,
          childrenLength = this._children.length;
        for (i = 0; i < childrenLength; i += 1) {
          child = this._children[i];
          if (child.getId() === indexOrId) {
            return child;
          }
        }
        throw new Error("No child found with id: " + indexOrId);
      } else {
        return this._children[indexOrId];
      }
    },
    getChildIndex: function getChildIndex(node) {
      var i,
        numChildren = this._children.length;
      for (i = 0; i < numChildren; i += 1) {
        if (this._children[i].getId() === node.getId()) {
          return i;
        }
      }
      return -1;
    },
    getChildren: function getChildren() {
      return this._children.map(function (child) {
        return child;
      });
    },
    hasDescendant: function hasDescendant(id) {
      var i,
        child,
        childrenLength = this._children.length;
      for (i = 0; i < childrenLength; i += 1) {
        child = this._children[i];
        if (child.getId() === id || child.hasDescendant(id)) {
          return true;
        }
      }
      childrenLength = this._detached.length;
      for (i = 0; i < childrenLength; i += 1) {
        child = this._detached[i];
        if (child.getId() === id || child.hasDescendant(id)) {
          return true;
        }
      }
      return false;
    },
    getDescendant: function getDescendant(id) {
      var i,
        child,
        childrenLength = this._children.length;
      for (i = 0; i < childrenLength; i += 1) {
        child = this._children[i];
        if (child._id === id) {
          return child;
        } else if (child.hasDescendant(id)) {
          return child.getDescendant(id);
        }
      }
      childrenLength = this._detached.length;
      for (i = 0; i < childrenLength; i += 1) {
        child = this._detached[i];
        if (child._id === id) {
          return child;
        } else if (child.hasDescendant(id)) {
          return child.getDescendant(id);
        }
      }
      throw new Error("No descendant found with id: " + id);
    },
    getNumberOfChildren: function getNumberOfChildren() {
      return this._children.length;
    },
    hasDetachedChildren: function hasDetachedChildren() {
      return this._detached.length > 0;
    },
    getDetachedChildren: function getDetachedChildren() {
      return this._detached.map(function (child) {
        return child;
      });
    },
    detach: function detach(originator) {
      if (!this.isDetached()) {
        var data = {
          child: this,
          index: this.getParent().getChildIndex(this)
        };
        var parent = this.getParent();
        this.getParent()._detachChild(this);
        parent.dispatchEvent("childRemoved", parent, data, originator);
        parent.dispatchEvent("childDetached", parent, data, originator);
      }
    },
    _detachChild: function _detachChild(peerNode) {
      this._removeChild(peerNode);
      this._detached.push(peerNode);
    },
    reattach: function reattach(index, originator) {
      if (this.isDetached()) {
        index = index >= 0 ? index : this.getParent().getNumberOfChildren();
        index = this.getParent()._reattachChild(this, index);
        this.getParent().dispatchEvent("childReattached", this.getParent(), {
          child: this,
          index: index
        }, originator);
      }
    },
    _reattachChild: function _reattachChild(peerNode, index) {
      var i = this._detached.indexOf(peerNode);
      this._detached.splice(i, 1);
      return this._addChild(peerNode, index);
    },
    isDetached: function isDetached() {
      return this.hasParent() && this.getParent().getDetachedChildren().indexOf(this) >= 0;
    },
    isAncestorDetached: function isAncestorDetached() {
      return this.hasParent() && (this.getParent().isDetached() || this.getParent().isAncestorDetached());
    },
    destroy: function destroy(originator) {
      if (this.hasParent()) {
        this.getParent().dispatchEvent("subTreeDestroyed", this.getParent(), {
          child: this
        }, originator);
      }
      this._destroyNodesRecursive(originator);
    },
    // deprecated
    remove: function remove() {
      this.destroy();
    },
    _destroyNodesRecursive: function _destroyNodesRecursive(originator) {
      try {
        this.getChildren().forEach(function (child) {
          child._destroyNodesRecursive(originator);
        });
        var index, oldParent;
        if (this.hasParent()) {
          oldParent = this.getParent();
          index = oldParent.getChildIndex(this);
          oldParent._removeChild(this);
          this._setParent(null);
          oldParent.dispatchEvent("childRemoved", oldParent, {
            child: this,
            index: index
          }, originator);
          oldParent.dispatchEvent("childDestroyed", oldParent, {
            child: this,
            index: index
          }, originator);
        }
        this.dispatchEvent("destroyed", this, undefined, originator);
      } finally {
        delete this._idCache[this._id];
      }
    },
    hasParent: function hasParent() {
      return !!this._parent;
    },
    getParent: function getParent() {
      return this._parent;
    },
    getRoot: function getRoot() {
      if (!this.hasParent()) {
        return this;
      } else {
        return this.getParent().getRoot();
      }
    },
    accept: function accept(visitor) {
      visitor(this);
      this._children.forEach(function (child) {
        child.accept(visitor);
      });
      this._detached.forEach(function (child) {
        child.accept(visitor);
      });
    },
    _addChild: function _addChild(node, index) {
      index = this._checkIndex(index);
      node._setParent(this);
      this._children.splice(index, 0, node);
      return index;
    },
    _removeChild: function _removeChild(nodeOrIndex) {
      var index, node;
      if (typeof nodeOrIndex === "number") {
        index = nodeOrIndex;
        node = this._children[index];
      } else {
        index = this.getChildIndex(nodeOrIndex);
        node = nodeOrIndex;
      }
      index = this._checkIndex(index);
      if (node) {
        this._children.splice(index, 1);
      }
    },
    _checkIndex: function _checkIndex(index) {
      if (index === undefined || index === null) {
        index = this._children.length;
      }
      return Math.max(0, Math.min(index, this._children.length));
    },
    _setParent: function _setParent(parent) {
      this._parent = parent;
    },
    toString: function toString(nesting) {
      nesting = nesting || 0; // handle no arguments

      var prop,
        key,
        i,
        sb = "";
      for (i = 0; i < nesting; i += 1) {
        sb += "    ";
      }
      sb += "{id='" + this._id + '\'';
      sb += ", type='" + this._type + '\'';
      sb += ", properties={";
      // manually iterate through the properties
      for (key in this._properties) {
        if (this._properties.hasOwnProperty(key)) {
          try {
            prop = dojo.toJson(this._properties[key]);
          } catch (e) {
            prop = this._properties[key].toString();
          }
          if (prop.length > 40) {
            prop = prop.slice(0, 40) + "...";
          }
          sb += key + ": " + prop + ", ";
        }
      }
      sb = sb.slice(0, sb.length - 2); // slice off the ending comma
      sb += "}, children=[";
      for (i = 0; i < this._children.length; i += 1) {
        // iterate through the children and print them out, nested
        sb += "\n" + this._children[i].toString(nesting + 1);
      }
      sb += "], detached=[";
      for (i = 0; i < this._detached.length; i += 1) {
        // iterate through the children and print them out, nested
        sb += "\n" + this._detached[i].toString(nesting + 1);
      }
      sb += "]}";
      return sb;
    },
    equals: function equals(peerNode) {
      var i;
      if (!peerNode) {
        return false;
      }
      if (this === peerNode) {
        return true;
      }
      if (this._id !== peerNode._id) {
        return false;
      }
      if (this._parent && peerNode._parent) {
        if (this._parent.getId() !== peerNode._parent.getId()) {
          return false;
        }
      } else {
        if (this._parent !== peerNode._parent) {
          return false;
        }
      }
      if (!this._propertiesEqual(this, peerNode)) {
        return false;
      }
      if (this._type !== peerNode._type) {
        return false;
      }
      if (this._children.length !== peerNode._children.length) {
        return false;
      }
      if (this._detached.length !== peerNode._detached.length) {
        return false;
      }
      for (i = 0; i < this._children.length; i += 1) {
        if (!this._children[i].equals(peerNode._children[i])) {
          return false;
        }
      }
      for (i = 0; i < this._detached.length; i += 1) {
        if (!this._detached[i].equals(peerNode._detached[i])) {
          return false;
        }
      }
      return true;
    },
    // Test if the _properties in two peernodes are equal,
    // this function will be overridden in the test code.
    _propertiesEqual: function _propertiesEqual(peerNodeA, peerNodeB) {
      if (peerNodeA._properties !== peerNodeB._properties) {
        return false;
      } else {
        return true;
      }
    }
  });
  PeerNode.createNode = PeerNodeImpl.createNode = function (type, properties, idCache) {
    var id = Utils.generateUuid();
    return new PeerNodeImpl(id, type, properties, idCache);
  };

  // export the impl as well as the interface
  PeerNode.Impl = PeerNodeImpl;
  return PeerNode;
});
//# sourceMappingURL=PeerNode.js.map
